import type { FC } from 'react'
import { 
  BookmarkIcon, 
  ClockIcon, 
  SparklesIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { Tooltip } from '@/components/common/Tooltip'

interface VerticalToolbarProps {
  showQueryLibrary: boolean
  showQueryHistory: boolean
  showAIAssistant: boolean
  onToggleQueryLibrary: () => void
  onToggleQueryHistory: () => void
  onToggleAIAssistant: () => void
  onFormatQuery?: () => void
  onExplainQuery?: () => void
  isExplainLoading?: boolean
}

export const VerticalToolbar: FC<VerticalToolbarProps> = ({
  showQueryLibrary,
  showQueryHistory,
  showAIAssistant,
  onToggleQueryLibrary,
  onToggleQueryHistory,
  onToggleAIAssistant,
  onFormatQuery,
  onExplainQuery,
  isExplainLoading = false,
}) => {
  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 w-12">
      <div className="flex flex-col items-center py-2 space-y-1">
        {/* Query Library Button */}
        <Tooltip content="Query Library" position="left">
          <button
            type="button"
            onClick={onToggleQueryLibrary}
            className={`
              p-2 rounded-md transition-colors w-9 h-9 flex items-center justify-center
              ${showQueryLibrary 
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
              }
            `}
            aria-label="Query Library"
          >
            <BookmarkIcon className="w-5 h-5" />
          </button>
        </Tooltip>

        {/* Query History Button */}
        <Tooltip content="Query History" position="left">
          <button
            type="button"
            onClick={onToggleQueryHistory}
            className={`
              p-2 rounded-md transition-colors w-9 h-9 flex items-center justify-center
              ${showQueryHistory 
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
              }
            `}
            aria-label="Query History"
          >
            <ClockIcon className="w-5 h-5" />
          </button>
        </Tooltip>

        {/* AI Assistant Button */}
        <Tooltip content="AI Assistant" position="left">
          <button
            type="button"
            onClick={onToggleAIAssistant}
            className={`
              p-2 rounded-md transition-colors w-9 h-9 flex items-center justify-center
              ${showAIAssistant 
                ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
              }
            `}
            aria-label="AI Assistant"
          >
            <SparklesIcon className="w-5 h-5" />
          </button>
        </Tooltip>

        {/* Separator */}
        <div className="w-8 h-px bg-gray-300 dark:bg-gray-700 my-1" />

        {/* Format Query Button */}
        {onFormatQuery && (
          <Tooltip content="Format Query" position="left">
            <button
              type="button"
              onClick={onFormatQuery}
              className="p-2 rounded-md transition-colors w-9 h-9 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
              aria-label="Format Query"
            >
              <DocumentTextIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* Explain Query Button */}
        {onExplainQuery && (
          <Tooltip content="Explain Query" position="left">
            <button
              type="button"
              onClick={onExplainQuery}
              disabled={isExplainLoading}
              className="p-2 rounded-md transition-colors w-9 h-9 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Explain Query"
            >
              <DocumentTextIcon className="w-5 h-5" />
              {isExplainLoading && (
                <span className="absolute bottom-0 right-0 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              )}
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  )
}